import { generatePath } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

export interface RoutePatternHandle {
  pattern?: string;
}

/**
 * Copies each route's own path pattern onto its `handle` so components can read
 * it back at runtime via `useRouteParamPath`.
 *
 * Reading the declared pattern is the only reliable way to rebuild the current
 * URL with a different param value. Reconstructing it from the rendered
 * pathname instead means guessing which segments were params, and that guess
 * fails two ways: param values are decoded while the pathname is encoded, and
 * static segments can follow a param (`/equipment/:location/guides`).
 *
 * Applied once to the whole route tree so a new route cannot forget to opt in.
 */
export function withRoutePatterns(routes: RouteObject[]): RouteObject[] {
  return routes.map(route => {
    const next = { ...route } as RouteObject & {
      children?: RouteObject[];
      handle?: RoutePatternHandle;
    };

    if (next.path) {
      next.handle = { ...next.handle, pattern: next.path };
    }
    if (next.children) {
      next.children = withRoutePatterns(next.children);
    }

    return next;
  });
}

/**
 * Rebuilds `pattern` from the current params with `overrides` applied, e.g.
 * swapping :location while staying on the same page. Params are URL-encoded by
 * `generatePath`, so values containing spaces round-trip correctly.
 */
export function buildRoutePath(
  pattern: string,
  params: Record<string, string | undefined>,
  overrides: Record<string, string | undefined> = {}
): string {
  const resolved = Object.entries({ ...params, ...overrides }).reduce<
    Record<string, string | null>
  >((acc, [key, value]) => {
    acc[key] = value ?? null;
    return acc;
  }, {});

  return generatePath(pattern, resolved);
}
